import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Navigate, useTypedParams } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { SearchLayoutRoot } from '@sorare/core/src/components/search/SearchLayout/ui';
import {
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_MY_CARDS_GALLERY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useDebouncedCallback } from '@sorare/core/src/hooks/useDebouncedCallback';
import { isRarityStr } from '@sorare/core/src/lib/so5';

import { CollectionNavigationSwitch } from 'components/myCards/CollectionNavigationSwitch';
import { CollectionSetBackground } from 'components/myCards/CollectionSetBackground';
import { LeagueCollectionPreview } from 'components/myCards/LeagueCollectionPreview';

import type { PageParams } from './__generated__/routeParams';

const Root = styled(SearchLayoutRoot)`
  position: static;
`;
const BackgroundWrapper = styled.div`
  position: absolute;
  isolation: isolate;
  inset: 0;
  & img {
    width: 100%;
    height: 100vh;
  }
  &:after {
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0) 50%,
      var(--c-black) 100%
    );
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100vh;
    z-index: 2;
  }
`;

const Header = styled(Horizontal).attrs({ gap: 0.5 })`
  padding: 0 var(--double-unit) var(--double-unit);
  border-bottom: 1px solid var(--c-nd-100);
`;

const Content = styled(Vertical)`
  padding: var(--double-unit) 0;
`;

const FOOTBALL_LEAGUES = [
  'premier-league-gb-eng',
  'ligue-1-fr',
  'laliga-es',
  'bundesliga-de',
];

const GalleryTypePage = () => {
  const { rarity, set } = useTypedParams<PageParams>();
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const navigateToSearch = (search: string) => {
    navigate(
      `${generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS_TYPE, {
        params: {
          type: rarity,
        },
      })}?${buildFilterQuery({
        [SEARCH_PARAMS.QUERY]: search,
      })}`
    );
  };

  const debouncedNavigateToSearch = useDebouncedCallback(navigateToSearch, 600);

  if (!isRarityStr(rarity)) {
    return (
      <Navigate to={generateSportContextPath(ANY_SPORT_MY_CARDS_GALLERY)} />
    );
  }
  return (
    <Root>
      <BackgroundWrapper>
        <CollectionSetBackground set={set} />
      </BackgroundWrapper>

      <Portal id="page-header-second-row">
        <Header>
          {!!currentUser && (
            <CollectionNavigationSwitch
              user={currentUser}
              rarity={Rarity.common}
            />
          )}
          <SearchInput
            small
            placeholder={formatMessage({
              id: 'GalleryTypePage.searchPlaceholder',
              defaultMessage: 'Filter by player or team',
            })}
            onChange={event => debouncedNavigateToSearch(event.target.value)}
          />
        </Header>
      </Portal>
      <Content gap={2}>
        {FOOTBALL_LEAGUES.map(league => (
          <LeagueCollectionPreview
            key={league}
            setSlug={set}
            leagueSlug={league}
            rarity={rarity}
            userSlug={currentUser?.slug || ''}
          />
        ))}
      </Content>
    </Root>
  );
};
export default GalleryTypePage;
